import { InputRule, Node } from '@tiptap/core';
import { Fragment } from '@tiptap/pm/model';
import { NodeSelection, Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { ReactNodeViewRenderer } from '@tiptap/react';
import BlankView from './BlankView.jsx';
import { blankSyntax, blanksEnabled, clozeRegex } from './blanks.js';

const WORD = /[\p{L}\p{N}'’-]/u;

function visibleAnswer(node) {
  return node.textContent || node.attrs.answer || '';
}

function blankSyntaxOf(node) {
  return blankSyntax(node.attrs.n, visibleAnswer(node), node.attrs.hint);
}

function maxBlankNumber(doc) {
  let max = 0;
  doc.descendants((node) => {
    if (node.type.name === 'blank') max = Math.max(max, Number(node.attrs.n) || 0);
    else if (node.isText && node.text?.includes('{{c')) {
      for (const m of node.text.matchAll(/\{\{c(\d+)::/g)) max = Math.max(max, Number(m[1]) || 0);
    }
  });
  return max;
}

function climb($pos) {
  for (let d = $pos.depth; d > 0; d -= 1) {
    const node = $pos.node(d);
    if (node.type.name === 'blank') return { pos: $pos.before(d), node };
  }
  return null;
}

export function blankAt(selection) {
  if (selection instanceof NodeSelection && selection.node.type.name === 'blank') {
    return { pos: selection.from, node: selection.node };
  }
  const from = climb(selection.$from);
  if (!from) return null;
  if (selection.empty) return from;
  const to = climb(selection.$to);
  if (!to || to.pos !== from.pos) return null;
  return from;
}

function containsBlank(doc, from, to) {
  let found = false;
  doc.nodesBetween(from, to, (node) => {
    if (node.type.name === 'blank') found = true;
  });
  return found;
}

function placeNear(tr, pos) {
  const at = Math.max(0, Math.min(pos, tr.doc.content.size));
  tr.setSelection(TextSelection.near(tr.doc.resolve(at)));
}

function unwrap(tr, { pos, node }) {
  const size = node.content.size;
  tr.replaceWith(pos, pos + node.nodeSize, node.content);
  placeNear(tr, pos + size);
  return true;
}

function allowsBlank($pos, type) {
  if (!$pos.parent.isTextblock) return false;
  const index = $pos.index();
  return $pos.parent.canReplaceWith(index, index, type);
}

function wrapRange(tr, state, from, to, n) {
  const type = state.schema.nodes.blank;
  const $from = state.doc.resolve(from);
  const $to = state.doc.resolve(to);
  if (from >= to || !$from.sameParent($to) || !allowsBlank($from, type)) return false;
  if (containsBlank(state.doc, from, to)) return false;
  const content = state.doc.slice(from, to).content;
  if (!content.size) return false;
  const node = type.create({ n, hint: '' }, content);
  tr.replaceWith(from, to, node);
  tr.setSelection(TextSelection.create(tr.doc, from + node.nodeSize - 1));
  return true;
}

function insertEmpty(tr, state, pos, n) {
  const type = state.schema.nodes.blank;
  const $pos = state.doc.resolve(pos);
  if (!allowsBlank($pos, type)) return false;
  tr.insert(pos, type.create({ n, hint: '' }));
  tr.setSelection(TextSelection.create(tr.doc, pos + 1));
  return true;
}

function wordRange(doc, pos) {
  const $pos = doc.resolve(pos);
  const parent = $pos.parent;
  if (!parent.isTextblock) return null;
  const base = $pos.start();
  let text = '';
  const starts = [];
  parent.forEach((node, offset) => {
    if (node.isText) {
      for (let i = 0; i < node.text.length; i += 1) {
        starts.push(base + offset + i);
        text += node.text[i];
      }
    } else {
      starts.push(base + offset);
      text += '\0';
    }
  });
  if (!text) return null;

  let idx = 0;
  while (idx < starts.length && starts[idx] < pos) idx += 1;
  let probe = idx < text.length && WORD.test(text[idx]) ? idx : idx - 1;
  if (probe < 0 || !WORD.test(text[probe] || '')) return null;
  let a = probe;
  let b = probe + 1;
  while (a > 0 && WORD.test(text[a - 1])) a -= 1;
  while (b < text.length && WORD.test(text[b])) b += 1;
  return { from: starts[a], to: starts[b - 1] + 1 };
}

function trimmedSelection(doc, from, to) {
  let a = from;
  let b = to;
  while (a < b) {
    const ch = doc.textBetween(a, a + 1, '\0', '\0');
    if (ch.length !== 1 || !/\s/.test(ch)) break;
    a += 1;
  }
  while (b > a) {
    const ch = doc.textBetween(b - 1, b, '\0', '\0');
    if (ch.length !== 1 || !/\s/.test(ch)) break;
    b -= 1;
  }
  return { from: a, to: b };
}

function applyBlank({ state, tr }, same) {
  const inside = blankAt(state.selection);
  const max = maxBlankNumber(state.doc);

  if (inside && same) {
    const n = Number(inside.node.attrs.n) || 1;
    return insertEmpty(tr, state, inside.pos + inside.node.nodeSize, n);
  }
  if (inside) return unwrap(tr, inside);

  const n = same ? Math.max(1, max) : max + 1;
  const { from, to, empty } = state.selection;
  if (!empty) {
    const range = trimmedSelection(state.doc, from, to);
    return wrapRange(tr, state, range.from, range.to, n);
  }
  const word = wordRange(state.doc, from);
  if (word && wrapRange(tr, state, word.from, word.to, n)) return true;
  return insertEmpty(tr, state, from, n);
}

function syntaxMatch(text) {
  const m = String(text ?? '').match(/^\{\{c(\d+)::([\s\S]*?)(?:::([\s\S]*?))?\}\}$/);
  if (!m || Number(m[1]) < 1) return null;
  return { n: Number(m[1]), answer: m[2] ?? '', hint: m[3] ?? '' };
}

function makeBlank(schema, n, answer, hint) {
  return schema.nodes.blank.create({ n, hint: hint || '' }, answer ? schema.text(answer) : null);
}

/** Replace finished `{{cN::answer}}` text with blanks, and repair a blank that is showing that syntax. */
function syntaxTransaction(state) {
  const replacements = [];
  state.doc.descendants((node, pos, parent) => {
    if (parent?.type.name === 'codeBlock') return false;
    if (node.type.name === 'blank') {
      const parsed = syntaxMatch(node.textContent);
      if (parsed) {
        replacements.push({
          from: pos,
          to: pos + node.nodeSize,
          content: makeBlank(state.schema, parsed.n, parsed.answer, parsed.hint || node.attrs.hint),
        });
      }
      return false;
    }
    if (!node.isText || !node.text?.includes('{{c')) return;
    if (node.marks.some((m) => m.type.name === 'code')) return;
    const parts = [];
    let last = 0;
    let hit = false;
    for (const m of node.text.matchAll(clozeRegex())) {
      const num = Number(m[1]);
      if (num < 1) continue;
      hit = true;
      if (m.index > last) parts.push(state.schema.text(node.text.slice(last, m.index), node.marks));
      parts.push(makeBlank(state.schema, num, m[2] ?? '', m[3] ?? ''));
      last = m.index + m[0].length;
    }
    if (!hit) return;
    if (last < node.text.length) parts.push(state.schema.text(node.text.slice(last), node.marks));
    replacements.push({ from: pos, to: pos + node.nodeSize, content: parts.length === 1 ? parts[0] : Fragment.from(parts) });
  });
  if (!replacements.length) return null;
  const tr = state.tr;
  for (let i = replacements.length - 1; i >= 0; i -= 1) {
    const { from, to, content } = replacements[i];
    tr.replaceWith(from, to, content);
  }
  tr.setMeta('blankSyntax', true);
  return tr;
}

export const Blank = Node.create({
  name: 'blank',
  group: 'inline',
  inline: true,
  content: 'text*',
  selectable: true,
  draggable: false,
  defining: true,
  marks: '',

  addAttributes() {
    return {
      n: { default: 1 },
      // Kept so cards saved before the word lived in the text still open.
      answer: { default: '' },
      hint: { default: '' },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-cloze]',
        getAttrs: (element) => {
          const text = element.textContent ?? '';
          const parsed = syntaxMatch(text.trim());
          return {
            n: Number(element.getAttribute('data-cloze')) || parsed?.n || 1,
            hint: element.getAttribute('data-hint') || parsed?.hint || '',
            answer: parsed?.answer || '',
          };
        },
      },
    ];
  },

  renderHTML({ node }) {
    return ['span', { 'data-cloze': String(node.attrs.n || 1), class: 'cloze-src' }, blankSyntaxOf(node)];
  },

  renderText({ node }) {
    return visibleAnswer(node);
  },

  addNodeView() {
    return ReactNodeViewRenderer(BlankView, {
      stopEvent: ({ event }) => {
        const target = event.target;
        return target instanceof Element && !!target.closest('button');
      },
    });
  },

  addCommands() {
    return {
      insertBlank:
        (same = false) =>
        ({ editor, state, tr }) => {
          if (!blanksEnabled(editor)) return false;
          return applyBlank({ state, tr }, same);
        },
    };
  },

  addKeyboardShortcuts() {
    const here = () => (blanksEnabled(this.editor) ? blankAt(this.editor.state.selection) : null);
    return {
      'Mod-Alt-b': () => this.editor.commands.insertBlank(false),
      'Mod-Alt-j': () => this.editor.commands.insertBlank(true),
      Backspace: () => {
        const hit = here();
        const { selection } = this.editor.state;
        if (!hit || !selection.empty || hit.node.content.size > 0) return false;
        const pos = hit.pos;
        return this.editor.commands.command(({ tr }) => {
          const current = tr.doc.nodeAt(pos);
          if (!current || current.type.name !== 'blank') return false;
          tr.delete(pos, pos + current.nodeSize);
          placeNear(tr, pos);
          return true;
        });
      },
      Enter: () => {
        const hit = here();
        if (!hit || !this.editor.state.selection.empty) return false;
        return this.editor.commands.setTextSelection(hit.pos + hit.node.nodeSize);
      },
    };
  },

  addInputRules() {
    const editor = this.editor;
    return [
      new InputRule({
        find: /\[\[([^\]\n]+)\]\]$/,
        handler: ({ state, range, match }) => {
          if (!blanksEnabled(editor)) return null;
          const answer = match[1].replace(/\s+/g, ' ').trim();
          if (!answer) return null;
          const n = maxBlankNumber(state.doc) + 1;
          const node = makeBlank(state.schema, n, answer, '');
          const { tr } = state;
          tr.replaceWith(range.from, range.to, node);
          placeNear(tr, range.from + node.nodeSize);
        },
      }),
    ];
  },

  addProseMirrorPlugins() {
    const editor = this.editor;
    return [
      new Plugin({
        key: new PluginKey('blankKeys'),
        props: {
          handleKeyDown(_view, event) {
            if (event.isComposing || event.key === 'Dead' || event.key === 'Process') return false;
            if (!(event.metaKey || event.ctrlKey) || !event.altKey || event.shiftKey) return false;
            const typed = (event.key || '').toLowerCase();
            if (event.code === 'KeyB' && typed !== 'b') return editor.commands.insertBlank(false);
            if (event.code === 'KeyJ' && typed !== 'j') return editor.commands.insertBlank(true);
            return false;
          },
        },
      }),
      new Plugin({
        key: new PluginKey('blankSyntax'),
        appendTransaction(transactions, _old, state) {
          if (!blanksEnabled(editor)) return null;
          if (!transactions.some((tr) => tr.docChanged)) return null;
          if (transactions.some((tr) => tr.getMeta('blankSyntax'))) return null;
          return syntaxTransaction(state);
        },
      }),
    ];
  },
});
